import { create } from "zustand";

/* =======================
   TYPES
======================= */

export type Job = {
  id: number;
  title: string;
  company: string;
  location: string;
  education: string;
  yearExperience: string;
  jobType: string;
  company_instagram: string;
  company_tiktok: string;
  company_facebook: string;
  company_linkedin: string;
  description: string;
  requirement: string;
  skill: string[];
  benefit: string[];
  galleyImage: string[];
  priceRange: string;
  company_logo?: string;
  officeAddress?: string;
  size?: string;
  map?: string;
};

type JobFilters = {
  keyword?: string;
  country_id?: string;
  state_id?: string;
  city_id?: string;
  industry_id?: string;
  job_type_id?: string;
  working_type_id?: string;
  degree_id?: string;
  experience_level_id?: string;
};

type ApplyPreview = {
  name: string;
  email: string;
  phone: string | null;
  photo: string | null;
  resume: string;
  resume_name: string;
  location: {
    city: string;
    state: string;
    country: string;
    full_location: string;
  };
};

interface SubmitApplyResult {
  success: boolean;
  message: string;
}

type JobStore = {
  jobs: Job[];
  filters: JobFilters;
  applyPreview?: ApplyPreview;

  page: number;
  hasMore: boolean;
  loading: boolean;

  setFilters: (filters: JobFilters) => void;
  fetchJobs: (lang: "en" | "id", loadMore?: boolean) => Promise<void>;
  fetchApplyPreview: (jobId: number) => Promise<void>;
  submitApply: (jobId: number) => Promise<SubmitApplyResult>;
  resetFilters: () => void;
};


/* =======================
   STORE
======================= */

export const useJobStore = create<JobStore>((set, get) => ({
  jobs: [],
  filters: {},
  applyPreview: undefined,
  page: 1,
  hasMore: true,
  loading: false,

  /* ---------- FILTER ---------- */
  setFilters: (filters) => {
    set({ filters, page: 1, jobs: [], hasMore: true });
  },
  
resetFilters: () => {
  set({
    filters: {},
    page: 1,
    jobs: [],
    hasMore: true,
  });
},


  /* ---------- FETCH JOBS ---------- */
  fetchJobs: async (lang, loadMore = false) => {
    try {
      const { filters, page } = get();

      const currentPage = loadMore ? page + 1 : 1;

      set({ loading: true });

      const params = new URLSearchParams();
      params.append("page", currentPage.toString());

      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const url = `https://admin.talentlinkindo.com/api/${lang}/vacancies?${params.toString()}`;

      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch jobs");

      const json = await res.json();

      // ⚠️ Kalau API pakai pagination format Laravel
      const rawData = Array.isArray(json.data)
        ? json.data
        : json.data?.data || [];

      const lastPage = json.data?.last_page || 1;
      const currentApiPage = json.data?.current_page || 1;

      const mapped: Job[] = rawData.map((item: any) => {
        const locationParts = [
          item.country?.name,
          item.state?.name,
          item.city?.name,
        ].filter(Boolean);

        const location = locationParts.join(", ");

        return {
          id: item.id,
          title: item.name?.[lang] || "-",
          company: item.company_name || "-",
          location,
          education: item.degree?.name?.[lang] || "-",
          yearExperience: item.experience_year?.name?.[lang] || "-",
          jobType: item.job_type?.name?.[lang] || "-",
          description: item.responsibility?.[lang] || "-",
          requirement: item.requirement?.[lang] || "-",
          skill: Array.isArray(item.skills)
            ? item.skills.map((s: any) => s.name)
            : [],
          benefit: Array.isArray(item.benefits)
            ? item.benefits.map((s: any) => s.name?.[lang])
            : [],
          galleyImage: item.company_gallery_images ?? [],
          priceRange: "Undisclosed",
          company_logo: item.company_logo || "-",
          company_instagram: item.company_instagram,
          company_tiktok: item.company_tiktok,
          company_facebook: item.company_facebook,
          company_linkedin: item.company_linkedin,
          size: item.company_size_name?.[lang] || "-",
          map: item.company_maps,
          officeAddress: item.company_address || "",
        };
      });

      set((state) => ({
        jobs: loadMore ? [...state.jobs, ...mapped] : mapped,
        page: currentApiPage,
        hasMore: currentApiPage < lastPage,
        loading: false,
      }));
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
      set({ loading: false });
    }
  },


  /* ---------- APPLY PREVIEW ---------- */
  fetchApplyPreview: async (jobId) => {
    try {
      const authRaw = localStorage.getItem("auth-storage");
      const token = authRaw
        ? JSON.parse(authRaw)?.state?.accessToken
        : null;

      if (!token) throw new Error("Unauthorized");

      const res = await fetch(
        `https://admin.talentlinkindo.com/api/candidate/vacancies/${jobId}/apply`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (!res.ok) throw new Error("Failed to fetch apply preview");

      const json = await res.json();
      set({ applyPreview: json.data });
    } catch (err) {
      console.error("fetchApplyPreview error:", err);
    }
  },

  /* ---------- SUBMIT APPLY ---------- */
  submitApply: async (jobId) => {
    try {
      const authRaw = localStorage.getItem("auth-storage");
      const token = authRaw
        ? JSON.parse(authRaw)?.state?.accessToken
        : null;

      if (!token) {
        return { success: false, message: "Unauthorized" };
      }

      const res = await fetch(
        `https://admin.talentlinkindo.com/api/candidate/vacancies/${jobId}/apply`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      const data = await res.json();

      return {
        success: res.ok,
        message: data?.message || "Application submitted successfully 🎉",
      };
    } catch {
      return { success: false, message: "Something went wrong" };
    }
  },
}));
